import { CheckedState } from '@radix-ui/react-checkbox';
import { Table } from '@tanstack/react-table';
import { useState } from 'react';
import { CheckboxLabeled } from '../../../../../design-system/checkbox/checkbox-labeled';
import { Stack } from '../../../../../design-system/stack/stack';
import { IS_PAID_CONFIDENCE_SCORE_COLUMN_ID } from './review-matches-table-columns-definition';

const HIGH_SCORE_BUCKET = 'HIGH';
const MEDIUM_SCORE_BUCKET = 'MEDIUM';
const LOW_SCORE_BUCKET = 'LOW';

type ConfidenceScoreBucket = 'HIGH' | 'MEDIUM' | 'LOW';
type ConfidenceScoreBucketRange = [number, number];

const CONFIDENCE_SCORE_BUCKET_RANGES: Record<ConfidenceScoreBucket, [number, number]> = {
  HIGH: [91, 100],
  MEDIUM: [71, 90],
  LOW: [0, 70],
};

type ConfidenceScoresFilterSectionProps<TData> = {
  table: Table<TData>;
};

export const ConfidenceScoresFilterSection = <TData,>({ table }: ConfidenceScoresFilterSectionProps<TData>) => {
  const initialBuckets = getInitialBuckets(table);

  const [selectedBuckets, setSelectedBuckets] = useState<Set<ConfidenceScoreBucket>>(new Set(initialBuckets));

  const setConfidenceBuckets = (buckets: ConfidenceScoreBucket[]) => {
    const ranges = buckets.map((b) => CONFIDENCE_SCORE_BUCKET_RANGES[b]);

    table.getColumn(IS_PAID_CONFIDENCE_SCORE_COLUMN_ID)?.setFilterValue(ranges.length ? ranges : undefined);
  };

  const toggleBucket = (bucket: ConfidenceScoreBucket, checked: CheckedState) => {
    setSelectedBuckets((prev) => {
      const next = new Set(prev);

      if (checked) {
        next.add(bucket);
      } else {
        next.delete(bucket);
      }

      setConfidenceBuckets([...next]);

      return next;
    });
  };

  return (
    <Stack space={1.5}>
      <h4 className="text-xs font-medium leading-5 tracking-tight text-seapoint-gray-text">Confidence scores</h4>

      <Stack space={2}>
        <CheckboxLabeled
          label="High"
          checked={selectedBuckets.has(HIGH_SCORE_BUCKET)}
          onCheckedChange={(v) => toggleBucket(HIGH_SCORE_BUCKET, v)}
        />

        <CheckboxLabeled
          label="Medium"
          checked={selectedBuckets.has(MEDIUM_SCORE_BUCKET)}
          onCheckedChange={(v) => toggleBucket(MEDIUM_SCORE_BUCKET, v)}
        />

        <CheckboxLabeled
          label="Low"
          checked={selectedBuckets.has(LOW_SCORE_BUCKET)}
          onCheckedChange={(v) => toggleBucket(LOW_SCORE_BUCKET, v)}
        />
      </Stack>
    </Stack>
  );
};

const getRangeBucket = (range: ConfidenceScoreBucketRange): ConfidenceScoreBucket | undefined => {
  if (range[0] === CONFIDENCE_SCORE_BUCKET_RANGES.HIGH[0] && range[1] === CONFIDENCE_SCORE_BUCKET_RANGES.HIGH[1]) {
    return 'HIGH';
  }

  if (range[0] === CONFIDENCE_SCORE_BUCKET_RANGES.MEDIUM[0] && range[1] === CONFIDENCE_SCORE_BUCKET_RANGES.MEDIUM[1]) {
    return 'MEDIUM';
  }

  if (range[0] === CONFIDENCE_SCORE_BUCKET_RANGES.LOW[0] && range[1] === CONFIDENCE_SCORE_BUCKET_RANGES.LOW[1]) {
    return 'LOW';
  }

  return undefined;
};

const getInitialBuckets = <TData,>(table: Table<TData>) => {
  const confidenceScoreFilter = table.getState().columnFilters.find((f) => f.id === IS_PAID_CONFIDENCE_SCORE_COLUMN_ID);

  const filterRanges = (confidenceScoreFilter?.value as ConfidenceScoreBucketRange[] | undefined) || [];

  return filterRanges.map(getRangeBucket).filter((b) => b !== undefined);
};
